import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import PublicMapLayout from "@/layouts/PublicMapLayout";
import LegalLayout from "@/layouts/LegalLayout";
import CompleteShowcasePage from "@/pages/public/CompleteShowcasePage";
import ArtifactShowcasePage from "@/pages/public/ArtifactShowcasePage";
import PremiumShowcasePage from "@/pages/public/PremiumShowcasePage";
import AuctionLandingPage from "@/pages/public/AuctionLandingPage";
import LandingPage from "@/pages/public/LandingPage";
import HomePage from "@/pages/public/HomePage";
import { EventHubPage, TimelinePage } from "@/pages/public/EventPages";
import { TicketsHubPage, TicketsSectionPage } from "@/pages/public/TicketsPages";
import { ArtifactsIndexPage, ArtifactDetailPage as LegacyArtifactDetailPage } from "@/pages/public/ArtifactsPages";
import { ResearchIndexPage, ResearchDetailPage } from "@/pages/public/ResearchPages";
import { FactsIndexPage, FactDetailPage } from "@/pages/public/FactsPages";
import { GovernancePage, CorrectionsLogPage, MethodologyPage, RightsPage, AccessibilityPage, PrivacyPage } from "@/pages/public/MiscPages";
import { CollectionShowcasePage, CollectionItemDetailPage, CollectionDossierPage } from "@/pages/public/CollectionShowcase";
import { CollectionGalleryPage } from "@/pages/public/CollectionGallery";
import { StudiesIndexPage, StudyDetailPage } from "@/pages/public/StudiesPages";
import AboutArchivePage from "@/pages/public/AboutArchivePage";
import ClaimDetailPage from "@/pages/public/ClaimDetailPage";
import SearchPage from "@/pages/public/SearchPage";
import ArchiveDownloadsPage from "@/pages/public/ArchiveDownloadsPage";
import SeatRegistryPage from "@/pages/public/SeatRegistryPage";
import TicketRegistryPage from "@/pages/public/TicketRegistryPage";
import CollectionBrowserPage from "@/pages/public/CollectionBrowserPage";
import ResearchHubPage from "@/pages/public/ResearchHubPage";
import ArtifactDetailPage from "@/pages/public/ArtifactDetailPage";
import CollectionGroupsPage from "@/pages/public/CollectionGroupsPage";
import CollectionGroupDetailPage from "@/pages/public/CollectionGroupDetailPage";
import NotFoundPage from "@/pages/public/NotFoundPage";

/* Wrap normal public pages in the canonical PublicLayout (header + footer + mobile tabs) */
const page = (element) => <PublicLayout>{element}</PublicLayout>;
/* Wrap immersive map/registry pages in the map shell (header + full-height workspace + mobile tabs) */
const mapPage = (element) => <PublicMapLayout>{element}</PublicMapLayout>;

const PublicRoutes = () => (
  <Routes>
    {/* =========================================================================
        PRIMARY PUBLIC NAVIGATION ROUTES
        Canonical structure: Collection, Arena, Tickets, Research, About
        ========================================================================= */}

    {/* HOME - Main editorial landing page */}
    <Route path="/" element={page(<CompleteShowcasePage />)} />

    {/* COLLECTION - Museum-style object browser */}
    <Route path="/collection" element={page(<CollectionBrowserPage />)} />
    <Route path="/collection/groups" element={page(<CollectionGroupsPage />)} />
    <Route path="/collection/groups/:slug" element={page(<CollectionGroupDetailPage />)} />
    <Route path="/collection/items/:itemSlug" element={page(<ArtifactDetailPage />)} />

    {/* ARENA (Ticket Registry Map) - Immersive full-screen map shell */}
    <Route path="/ticket-registry/map" element={mapPage(<TicketRegistryPage view="map" />)} />

    {/* TICKETS (Ticket Registry List/Detail) - Part of the immersive registry experience */}
    <Route path="/ticket-registry/list" element={mapPage(<TicketRegistryPage view="list" />)} />
    <Route path="/ticket-registry/tickets/:registryReference" element={mapPage(<TicketRegistryPage view="detail" />)} />

    {/* RESEARCH - Historical scholarship area */}
    <Route path="/research" element={page(<ResearchHubPage />)} />
    <Route path="/research/topics/:topicSlug" element={page(<ResearchHubPage view="topic" />)} />
    <Route path="/research/studies/:studySlug" element={page(<ResearchHubPage view="study" />)} />

    {/* ABOUT - Archive information */}
    <Route path="/about" element={page(<AboutArchivePage />)} />
    <Route path="/about-archive" element={<Navigate to="/about" replace />} />

    {/* SEARCH - Global archive search (linked from header) */}
    <Route path="/search" element={page(<SearchPage />)} />

    {/* =========================================================================
        LEGACY ROUTE REDIRECTS (Old URLs → New canonical structure)
        ========================================================================= */}

    {/* Old Arena/Tickets shortcuts → Canonical routes */}
    <Route path="/arena" element={<Navigate to="/ticket-registry/map" replace />} />
    <Route path="/tickets" element={<Navigate to="/ticket-registry/list" replace />} />
    <Route path="/tickets/:registryReference" element={<Navigate to="/ticket-registry/tickets/:registryReference" replace />} />

    {/* Other legacy redirects */}
    <Route path="/ticket-registry" element={<Navigate to="/ticket-registry/list" replace />} />
    <Route path="/registry" element={<Navigate to="/ticket-registry/list" replace />} />
    <Route path="/seat-map" element={<Navigate to="/ticket-registry/map" replace />} />
    <Route path="/event" element={<Navigate to="/" replace />} />
    <Route path="/collection-story" element={<Navigate to="/" replace />} />
    <Route path="/event/timeline" element={<Navigate to="/research" replace />} />
    <Route path="/timeline" element={<Navigate to="/research" replace />} />
    <Route path="/artifacts" element={<Navigate to="/collection" replace />} />
    <Route path="/catalog" element={<Navigate to="/collection" replace />} />
    <Route path="/studies" element={<Navigate to="/research" replace />} />

    {/* Legacy seat registry */}
    <Route path="/seat-registry" element={page(<SeatRegistryPage />)} />

    {/* Previous versions (backup/internal) */}
    <Route path="/showcase-v2" element={<ArtifactShowcasePage />} />
    <Route path="/showcase-v1" element={<PremiumShowcasePage />} />
    <Route path="/auction-preview" element={<AuctionLandingPage />} />
    <Route path="/marketing" element={<LandingPage />} />

    {/* Multi-page archive structure - wrapped in PublicLayout (BACKUP) */}
    <Route path="/archive/*" element={
      <PublicLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/event" element={<EventHubPage />} />
          <Route path="/event/timeline" element={<TimelinePage />} />
          <Route path="/tickets" element={<TicketsHubPage />} />
          <Route path="/tickets/:section" element={<TicketsSectionPage />} />
          <Route path="/collection" element={<CollectionGalleryPage />} />
          <Route path="/collection-story" element={<CollectionShowcasePage />} />
          <Route path="/collection/item/:slug" element={<CollectionItemDetailPage />} />
          <Route path="/collection-dossier" element={<CollectionDossierPage />} />
          <Route path="/studies" element={<StudiesIndexPage />} />
          <Route path="/studies/:slug" element={<StudyDetailPage />} />
          <Route path="/about-archive" element={<AboutArchivePage />} />
          <Route path="/artifacts" element={<ArtifactsIndexPage />} />
          <Route path="/artifacts/:slug" element={<ArtifactDetailPage />} />
          <Route path="/research" element={<ResearchIndexPage />} />
          <Route path="/research/:slug" element={<ResearchDetailPage />} />
          <Route path="/facts" element={<FactsIndexPage />} />
          <Route path="/facts/:slug" element={<FactDetailPage />} />
          <Route path="/claims/:claimId" element={<ClaimDetailPage />} />
          <Route path="/governance" element={<GovernancePage />} />
          <Route path="/governance/methodology" element={<MethodologyPage />} />
          <Route path="/corrections-log" element={<CorrectionsLogPage />} />
          <Route path="/archives" element={<ArchiveDownloadsPage />} />
        </Routes>
      </PublicLayout>
    } />

    {/* Standalone legal pages */}
    <Route path="/rights" element={<LegalLayout><RightsPage /></LegalLayout>} />
    <Route path="/accessibility" element={<LegalLayout><AccessibilityPage /></LegalLayout>} />
    <Route path="/privacy" element={<LegalLayout><PrivacyPage /></LegalLayout>} />
    <Route path="/governance" element={page(<GovernancePage />)} />
    <Route path="/governance/methodology" element={page(<MethodologyPage />)} />
    <Route path="/corrections-log" element={page(<CorrectionsLogPage />)} />
    <Route path="/facts" element={page(<FactsIndexPage />)} />
    <Route path="/facts/:slug" element={page(<FactDetailPage />)} />

    {/* Catch-all 404 - must be last */}
    <Route path="*" element={page(<NotFoundPage />)} />
  </Routes>
);

export default PublicRoutes;
